import React, { Component } from 'react';
import AthleticColorChart from './AthleticColorChart';

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    sectionLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '8px',
    },
    patternGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '6px',
    },
    patternBtn: (isActive) => ({
        padding: '10px 4px',
        background: isActive ? '#2a2a2a' : '#1a1a1a',
        border: isActive ? '2px solid #ecff19' : '2px solid #333',
        borderRadius: '6px',
        color: isActive ? '#ecff19' : '#aaa',
        fontSize: '11px',
        fontWeight: isActive ? '600' : '400',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'border-color 0.15s, color 0.15s',
    }),
    slotRow: {
        display: 'flex',
        gap: '10px',
        marginBottom: '10px',
        flexWrap: 'wrap',
    },
    slotWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
    },
    slotBtn: (color, isActive) => ({
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: color,
        border: isActive ? '3px solid #ecff19' : '3px solid transparent',
        outline: color === '#ffffff' || color === '#fff'
            ? '1px solid #444'
            : isActive ? '1px solid #555' : 'none',
        boxSizing: 'border-box',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'border-color 0.15s',
        position: 'relative',
    }),
    activeDot: {
        position: 'absolute',
        bottom: '-7px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        background: '#ecff19',
    },
    slotLabel: {
        fontSize: '10px',
        color: '#666',
        textAlign: 'center',
        marginTop: '2px',
        whiteSpace: 'nowrap',
    },
    loadingMsg: {
        fontSize: '11px',
        color: '#555',
        fontStyle: 'italic',
    },
    divider: {
        borderTop: '1px solid #2a2a2a',
        margin: '0 0 2px',
    },
};

/**
 * PatternPicker
 *
 * Props:
 *   patterns           – [{ id, label, url }]  available patterns from the API
 *   selectedPatternId  – id of the active pattern
 *   patternOriginals   – { [patternId]: ColorSlot[] }  discovered slots per pattern
 *   globalColors       – string[]  shared palette; index maps directly to slot position
 *   onPatternChange    – (patternId) => void
 *   onColorChange      – (patternId, slotIndex, hex) => void
 */
class PatternPicker extends Component {
    constructor(props) {
        super(props);
        this.state = { activeSlot: 0 };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedPatternId !== this.props.selectedPatternId) {
            this.setState({ activeSlot: 0 });
        }
    }

    handlePatternSelect = (patternId) => {
        this.props.onPatternChange(patternId);
        this.setState({ activeSlot: 0 });
    };

    handleColorSelect = (hex) => {
        const { selectedPatternId, onColorChange } = this.props;
        const { activeSlot } = this.state;
        onColorChange(selectedPatternId, activeSlot, hex);
    };

    render() {
        const { patterns = [], selectedPatternId, patternOriginals, globalColors } = this.props;
        const { activeSlot } = this.state;

        const slots = patternOriginals[selectedPatternId] || [];
        const originalColors = slots.map((s) => s.color);
        const colors = slots.map((s, i) => globalColors[i] !== undefined ? globalColors[i] : s.color);
        const colorCount = slots.length;
        const clampedSlot = Math.min(activeSlot, Math.max(0, colorCount - 1));

        return (
            <div style={styles.root}>

                {/* ── Pattern selector ── */}
                <div>
                    <div style={styles.sectionLabel}>Pattern</div>
                    <div style={styles.patternGrid}>
                        {patterns.map((p) => (
                            <button
                                key={p.id}
                                style={styles.patternBtn(selectedPatternId === p.id)}
                                onClick={() => this.handlePatternSelect(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Color slots ── */}
                {colorCount === 0 ? (
                    <div style={styles.loadingMsg}>Loading colors…</div>
                ) : (
                    <div>
                        <div style={styles.sectionLabel}>
                            {colorCount === 1 ? 'Color' : `Color ${clampedSlot + 1} of ${colorCount}`}
                        </div>
                        <div style={styles.slotRow}>
                            {Array.from({ length: colorCount }, (_, i) => {
                                const c = colors[i] || originalColors[i] || '#888888';
                                const isActive = clampedSlot === i;
                                return (
                                    <div key={i} style={styles.slotWrap}>
                                        <div
                                            style={styles.slotBtn(c, isActive)}
                                            onClick={() => this.setState({ activeSlot: i })}
                                            title={`Slot ${i + 1}: ${c}`}
                                        >
                                            {isActive && <div style={styles.activeDot} />}
                                        </div>
                                        <span style={styles.slotLabel}>{i + 1}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={styles.divider} />

                        {/* ── Color chart ── */}
                        <AthleticColorChart
                            selectedColor={colors[clampedSlot] || originalColors[clampedSlot]}
                            onSelect={this.handleColorSelect}
                        />
                    </div>
                )}

            </div>
        );
    }
}

export default PatternPicker;
